import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { GROCERY_LIST_QUERY_KEY } from './useGroceryList'
import { groceryListService } from './grocery-list.service'
import { showToast } from '@/shared/utils/toast'
import type { GroceryList, AddAdHocItemRequest } from './types'

export function useAddAdHocItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('grocery-list')

  return useMutation({
    mutationFn: (request: AddAdHocItemRequest) =>
      groceryListService.addAdHocItem(request),

    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: GROCERY_LIST_QUERY_KEY })
      const snapshot = queryClient.getQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY)

      // Optimistic update: add a temporary item
      const tempId = crypto.randomUUID()
      queryClient.setQueryData<GroceryList>(GROCERY_LIST_QUERY_KEY, (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              id: tempId,
              ingredientId: null,
              ingredientName: request.name,
              quantity: request.quantity ?? 0,
              canonicalUnit: request.unit ?? '',
              aisleCategory: 'Other',
              ticked: false,
              isAdHoc: true,
            },
          ],
        }
      })

      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(GROCERY_LIST_QUERY_KEY, context.snapshot)
      }
      showToast(t('addItem.error'))
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: GROCERY_LIST_QUERY_KEY })
    },
  })
}
